import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { 
  ArrowRight, 
  Check, 
  Minus,
  Zap, 
  Shield, 
  ShieldCheck,
  FileText, 
  Users, 
  ChevronDown,
  Star,
  Play,
  CreditCard,
  Receipt,
  Repeat,
  ShoppingBag,
  QrCode,
  Calculator,
  PiggyBank,
  Send,
  TrendingUp,
  Upload,
  Globe,
  Eye,
  UsersRound,
  FileSpreadsheet,
  UserCog,
  BarChart3,
  Bot,
  CalendarDays,
  Clock,
  Layers,
  Network,
  Boxes,
  LinkIcon,
  Smartphone,
  FolderKanban
} from 'lucide-react';
import { useState } from 'react';
import InvoiceDemo from '../components/InvoiceDemo';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);

  const advancedFeatures = [
    { icon: QrCode, titleKey: "landing.advQrBill", descKey: "landing.advQrBillDesc", color: "bg-red-500" },
    { icon: UserCog, titleKey: "landing.advHrPayroll", descKey: "landing.advHrPayrollDesc", color: "bg-emerald-500" },
    { icon: BarChart3, titleKey: "landing.advHrDashboard", descKey: "landing.advHrDashboardDesc", color: "bg-indigo-500" },
    { icon: Bot, titleKey: "landing.advAiChatbot", descKey: "landing.advAiChatbotDesc", color: "bg-violet-500" },
    { icon: Repeat, titleKey: "landing.advRecurring", descKey: "landing.advRecurringDesc", color: "bg-orange-500" },
    { icon: ShoppingBag, titleKey: "landing.advShopify", descKey: "landing.advShopifyDesc", color: "bg-pink-500" },
    { icon: Globe, titleKey: "landing.advMultiCurrency", descKey: "landing.advMultiCurrencyDesc", color: "bg-blue-500" },
    { icon: UsersRound, titleKey: "landing.advMultiUser", descKey: "landing.advMultiUserDesc", color: "bg-cyan-500" },
    { icon: CreditCard, titleKey: "landing.advPartialPayments", descKey: "landing.advPartialPaymentsDesc", color: "bg-teal-500" },
    { icon: Receipt, titleKey: "landing.advCreditNotes", descKey: "landing.advCreditNotesDesc", color: "bg-purple-500" },
    { icon: Send, titleKey: "landing.advEmailSend", descKey: "landing.advEmailSendDesc", color: "bg-sky-500" },
    { icon: FileSpreadsheet, titleKey: "landing.advAccountingExport", descKey: "landing.advAccountingExportDesc", color: "bg-amber-500" },
    { icon: Upload, titleKey: "landing.advCsvImport", descKey: "landing.advCsvImportDesc", color: "bg-lime-500" },
    { icon: CalendarDays, titleKey: "landing.advAbsences", descKey: "landing.advAbsencesDesc", color: "bg-rose-500" },
    { icon: Eye, titleKey: "landing.advPdfPreview", descKey: "landing.advPdfPreviewDesc", color: "bg-slate-500" },
    { icon: PiggyBank, titleKey: "landing.advExpenseTracking", descKey: "landing.advExpenseTrackingDesc", color: "bg-green-500" },
    { icon: Boxes, titleKey: "landing.advStock", descKey: "landing.advStockDesc", color: "bg-blue-600" },
    { icon: LinkIcon, titleKey: "landing.advPortal", descKey: "landing.advPortalDesc", color: "bg-cyan-600" },
    { icon: Smartphone, titleKey: "landing.advPwa", descKey: "landing.advPwaDesc", color: "bg-gray-600" },
    { icon: FolderKanban, titleKey: "landing.advProjects", descKey: "landing.advProjectsDesc", color: "bg-indigo-600" },
    { icon: BarChart3, titleKey: "landing.advProjectReports", descKey: "landing.advProjectReportsDesc", color: "bg-amber-600" }
  ];

  const testimonials = [
    {
      name: "Marc Dubois",
      role: "Consultant IT, Geneve",
      image: "https://images.unsplash.com/photo-1632255657991-ce622acebecd?w=150&h=150&fit=crop",
      quote: "J'ai divise par 3 le temps passe sur ma facturation. Simple, efficace, suisse."
    },
    {
      name: "Sophie Laurent",
      role: "Architecte, Lausanne",
      image: "https://images.pexels.com/photos/6077795/pexels-photo-6077795.jpeg?w=150&h=150&fit=crop",
      quote: "Enfin un logiciel qui comprend les specificites suisses. Les 20 templates sont magnifiques."
    },
    {
      name: "Thomas Muller",
      role: "Electricien, Zurich",
      image: "https://images.unsplash.com/photo-1737574821698-862e77f044c1?w=150&h=150&fit=crop",
      quote: "Mes clients recoivent des factures professionnelles. Ca change tout pour mon image."
    }
  ];

  const stats = [
    { value: "2'500+", labelKey: "landing.statInvoicesCreated" },
    { value: "500+", labelKey: "landing.statCompanies" },
    { value: "99.9%", labelKey: "landing.statUptime" },
    { value: "4.9/5", labelKey: "landing.statSatisfaction" }
  ];

  const faqs = [
    { qKey: "landing.faq1Q", aKey: "landing.faq1A" },
    { qKey: "landing.faq2Q", aKey: "landing.faq2A" },
    { qKey: "landing.faq3Q", aKey: "landing.faq3A" },
    { qKey: "landing.faq4Q", aKey: "landing.faq4A" },
    { qKey: "landing.faq5Q", aKey: "landing.faq5A" },
    { qKey: "landing.faq6Q", aKey: "landing.faq6A" },
    { qKey: "landing.faq7Q", aKey: "landing.faq7A" },
    { qKey: "landing.faq8Q", aKey: "landing.faq8A" }
  ];

  // Comparison table data
  const comparisonCategories = [
    {
      nameKey: "landing.compInvoicing",
      features: [
        { nameKey: "landing.compInvoicesQuotes", starter: "check", pro: "check", enterprise: "check" },
        { nameKey: "landing.compQrBill", starter: "check", pro: "check", enterprise: "check" },
        { nameKey: "landing.compPdfTemplates", starter: "landing.comp5Templates", pro: "landing.comp20Templates", enterprise: "landing.comp20Templates" },
        { nameKey: "landing.compRecurring", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compPartialPayments", starter: "check", pro: "check", enterprise: "check" },
        { nameKey: "landing.compCreditNotes", starter: "check", pro: "check", enterprise: "check" },
        { nameKey: "landing.compEmailSend", starter: "minus", pro: "check", enterprise: "check" },
      ]
    },
    {
      nameKey: "landing.compManagement",
      features: [
        { nameKey: "landing.compClientCrm", starter: "check", pro: "check", enterprise: "check" },
        { nameKey: "landing.compExpenses", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compAccountingExport", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compMultiCurrency", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compMultiUsers", starter: "landing.comp1User", pro: "landing.compUpTo5", enterprise: "landing.compUnlimited" },
        { nameKey: "landing.compDashboard", starter: "check", pro: "check", enterprise: "check" },
        { nameKey: "landing.compPdfPreview", starter: "check", pro: "check", enterprise: "check" },
        { nameKey: "landing.compCsvImport", starter: "minus", pro: "check", enterprise: "check" },
      ]
    },
    {
      nameKey: "landing.compAdvanced",
      features: [
        { nameKey: "landing.compHrPayroll", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compShopify", starter: "minus", pro: "minus", enterprise: "check" },
        { nameKey: "landing.compAiAssistant", starter: "minus", pro: "minus", enterprise: "check" },
        { nameKey: "landing.compAbsences", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compAuditLog", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compStock", starter: "minus", pro: "minus", enterprise: "check" },
        { nameKey: "landing.compPortal", starter: "minus", pro: "minus", enterprise: "check" },
        { nameKey: "landing.compPwa", starter: "minus", pro: "minus", enterprise: "check" },
        { nameKey: "landing.compProjects", starter: "minus", pro: "check", enterprise: "check" },
        { nameKey: "landing.compProjectReports", starter: "minus", pro: "minus", enterprise: "check" },
        { nameKey: "landing.compSupport", starter: "landing.compEmailOnly", pro: "landing.compPriority", enterprise: "landing.compDedicated" },
      ]
    }
  ];

  const renderCompCell = (value) => {
    if (value === "check") return <Check className="w-5 h-5 text-emerald-500 mx-auto" />;
    if (value === "minus") return <Minus className="w-5 h-5 text-zinc-300 mx-auto" />;
    return <span className="text-sm font-medium text-zinc-700">{t(value)}</span>;
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-200"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/xyron-logo.png" alt="Xyron Core ERP" className="h-10 w-10 rounded-lg object-contain" />
            <div className="flex flex-col">
              <span className="font-bold text-xl tracking-tight leading-tight">Xyron Core ERP</span>
              <span className="text-[10px] text-zinc-500 leading-tight">ERP Suisse pour PME</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Fonctionnalites</a>
            <a href="#testimonials" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Temoignages</a>
            <a href="#pricing" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">Tarifs</a>
            <a href="#faq" className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="minimal" />
            <Button 
              variant="ghost" 
              className="text-sm"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
            >
              {t('nav.login')}
            </Button>
            <Button 
              className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm rounded-sm hover:-translate-y-0.5 transition-all"
              onClick={() => navigate('/register')}
              data-testid="nav-register-btn"
            >
              {t('landing.startFree')}
            </Button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="relative z-10"
            >
              <motion.div variants={fadeInUp} className="mb-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 text-red-600 text-xs font-bold tracking-wider uppercase rounded-sm">
                  <QrCode className="w-3 h-3" />
                  QR-Facture Suisse
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-600 text-xs font-bold tracking-wider uppercase rounded-sm">
                  <UserCog className="w-3 h-3" />
                  RH & Paie
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold tracking-wider uppercase rounded-sm">
                  <Bot className="w-3 h-3" />
                  Assistant IA
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 text-purple-600 text-xs font-bold tracking-wider uppercase rounded-sm">
                  <Globe className="w-3 h-3" />
                  FR / DE / EN
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-bold tracking-wider uppercase rounded-sm">
                  <FolderKanban className="w-3 h-3" />
                  {t('landing.advProjects')}
                </span>
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-none text-zinc-900 mb-6"
              >
                {t('landing.heroTitle')}<br />
                <span className="text-red-600">{t('landing.heroTitleHighlight')}</span>
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-lg sm:text-xl text-zinc-600 mb-8 max-w-lg leading-relaxed"
              >
                {t('landing.heroSubtitle')}
              </motion.p>
              
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white text-base px-8 py-6 rounded-sm hover:-translate-y-1 transition-all shadow-lg shadow-red-200"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  {t('landing.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="text-base px-8 py-6 rounded-sm border-2 border-zinc-200 hover:border-zinc-300"
                  onClick={() => navigate('/pricing')}
                  data-testid="hero-pricing-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {t('landing.watchDemo')}
                </Button>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="mt-8 flex items-center gap-6">
                <div className="flex -space-x-3">
                  {testimonials.map((testimonial, i) => (
                    <img 
                      key={i}
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-zinc-500">500+ entreprises suisses</p>
                </div>
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <InvoiceDemo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Marquee */}
      <section className="py-8 bg-zinc-900 text-white">
        <Marquee gradient={false} speed={50} className="overflow-hidden">
          {[...stats, ...stats].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 mx-12">
              <span className="text-3xl font-bold tracking-tight">{stat.value}</span>
              <span className="text-zinc-400 text-sm">{t(stat.labelKey)}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* ===== REDESIGNED FEATURES SECTION ===== */}
      <section id="features" className="py-24 md:py-32 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4 block">
              {t('landing.features')}
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
              {t('landing.featuresTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-zinc-600 max-w-2xl mx-auto">
              {t('landing.featuresSubtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5"
          >
            {/* Card 1: Create in 2 min - LARGE */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-8 bg-white border border-zinc-200 p-8 md:p-10 hover:border-red-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/5 transition-all duration-300 group"
              data-testid="feature-card-speed"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Clock className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900">{t('landing.feature1Title')}</h3>
              </div>
              <p className="text-zinc-600 mb-8 max-w-md">{t('landing.feature1Desc')}</p>
              <div className="flex items-end gap-2 select-none" aria-hidden="true">
                <span className="text-8xl md:text-9xl font-black tracking-tighter text-zinc-900/5 leading-none">2</span>
                <span className="text-4xl md:text-5xl font-bold tracking-tight text-red-600/20 leading-none mb-2">MIN</span>
              </div>
            </motion.div>

            {/* Card 2: Swiss Security */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-4 bg-white border border-zinc-200 p-8 hover:border-red-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/5 transition-all duration-300 group relative overflow-hidden"
              data-testid="feature-card-security"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <ShieldCheck className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900">{t('landing.feature2Title')}</h3>
              </div>
              <p className="text-zinc-600 mb-6">{t('landing.feature2Desc')}</p>
              {/* Abstract Swiss cross */}
              <div className="absolute bottom-4 right-4 opacity-[0.04] select-none" aria-hidden="true">
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <rect x="45" y="15" width="30" height="90" fill="currentColor" className="text-red-600" />
                  <rect x="15" y="45" width="90" height="30" fill="currentColor" className="text-red-600" />
                </svg>
              </div>
            </motion.div>

            {/* Card 3: 20+ PDF Templates */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-4 bg-white border border-zinc-200 p-8 hover:border-red-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/5 transition-all duration-300 group relative overflow-hidden"
              data-testid="feature-card-templates"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Layers className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900">{t('landing.feature3Title')}</h3>
              </div>
              <p className="text-zinc-600 mb-4">{t('landing.feature3Desc')}</p>
              {/* Abstract stacked documents */}
              <div className="flex gap-2 mt-4" aria-hidden="true">
                {[...Array(4)].map((_, i) => (
                  <div 
                    key={i} 
                    className="w-12 h-16 border border-zinc-200 rounded-sm bg-zinc-50 group-hover:border-red-200 transition-all"
                    style={{ transform: `rotate(${(i - 1.5) * 3}deg)`, opacity: 1 - i * 0.15 }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Card 4: Client Management */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-4 bg-white border border-zinc-200 p-8 hover:border-red-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/5 transition-all duration-300 group relative overflow-hidden"
              data-testid="feature-card-clients"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Network className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900">{t('landing.feature4Title')}</h3>
              </div>
              <p className="text-zinc-600 mb-4">{t('landing.feature4Desc')}</p>
              {/* Abstract network nodes */}
              <div className="flex items-center justify-center gap-3 mt-4" aria-hidden="true">
                <div className="w-8 h-8 rounded-full border-2 border-zinc-200 group-hover:border-red-300 transition-colors" />
                <div className="w-12 h-0.5 bg-zinc-200 group-hover:bg-red-200 transition-colors" />
                <div className="w-10 h-10 rounded-full border-2 border-zinc-300 bg-zinc-50 group-hover:border-red-400 transition-colors" />
                <div className="w-12 h-0.5 bg-zinc-200 group-hover:bg-red-200 transition-colors" />
                <div className="w-8 h-8 rounded-full border-2 border-zinc-200 group-hover:border-red-300 transition-colors" />
              </div>
            </motion.div>

            {/* Card 5: HR & Payroll */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-4 bg-white border border-zinc-200 p-8 hover:border-red-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/5 transition-all duration-300 group"
              data-testid="feature-card-hr"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <Calculator className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900">{t('landing.feature5Title')}</h3>
              </div>
              <p className="text-zinc-600 text-sm leading-relaxed">{t('landing.feature5Desc')}</p>
            </motion.div>

            {/* Card 6: QR-Bill - wide */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-6 bg-white border border-zinc-200 p-8 hover:border-red-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/5 transition-all duration-300 group relative overflow-hidden"
              data-testid="feature-card-qrbill"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <QrCode className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900">{t('landing.advQrBill')}</h3>
              </div>
              <p className="text-zinc-600">{t('landing.advQrBillDesc')}</p>
              {/* Abstract QR pattern */}
              <div className="absolute bottom-3 right-3 opacity-[0.06] select-none" aria-hidden="true">
                <div className="grid grid-cols-5 gap-1">
                  {[...Array(25)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`w-4 h-4 rounded-sm ${[0,1,2,4,5,6,10,14,18,19,20,22,23,24].includes(i) ? 'bg-zinc-900' : ''}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Card 7: Shopify - wide */}
            <motion.div
              variants={scaleIn}
              className="lg:col-span-6 bg-white border border-zinc-200 p-8 hover:border-red-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-500/5 transition-all duration-300 group"
              data-testid="feature-card-shopify"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors">
                  <ShoppingBag className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-zinc-900">{t('landing.advShopify')}</h3>
              </div>
              <p className="text-zinc-600">{t('landing.advShopifyDesc')}</p>
            </motion.div>
          </motion.div>

          {/* Inline extra features chips */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mt-8 flex flex-wrap justify-center gap-3"
          >
            {[
              { icon: Globe, label: t('landing.advMultiCurrency') },
              { icon: UsersRound, label: t('landing.advMultiUser') },
              { icon: Repeat, label: t('landing.advRecurring') },
              { icon: Bot, label: t('landing.advAiChatbot') },
              { icon: PiggyBank, label: t('landing.advExpenseTracking') },
              { icon: Boxes, label: t('landing.advStock') },
              { icon: LinkIcon, label: t('landing.advPortal') },
              { icon: Smartphone, label: t('landing.advPwa') },
            ].map((chip, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 rounded-full text-sm text-zinc-700 hover:border-red-200 hover:text-red-600 transition-colors cursor-default">
                <chip.icon className="w-4 h-4" />
                {chip.label}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4 block">
              {t('landing.howItWorks')}
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
              {t('landing.threeSteps')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              { step: "01", titleKey: "landing.step1Title", descKey: "landing.step1Desc" },
              { step: "02", titleKey: "landing.step2Title", descKey: "landing.step2Desc" },
              { step: "03", titleKey: "landing.step3Title", descKey: "landing.step3Desc" }
            ].map((item, index) => (
              <motion.div key={index} variants={fadeInUp} className="text-center">
                <div className="w-20 h-20 bg-zinc-900 text-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-zinc-900 mb-2">{t(item.titleKey)}</h3>
                <p className="text-zinc-600">{t(item.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Advanced Features Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-full mb-6">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-300">{t('landing.newFeatures')}</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t('landing.newFeaturesTitle')}<br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                {t('landing.newFeaturesTitleHighlight')}
              </span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-zinc-400 max-w-2xl mx-auto">
              {t('landing.newFeaturesSubtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {advancedFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
              >
                <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t(feature.titleKey)}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{t(feature.descKey)}</p>
                <div className={`absolute inset-0 ${feature.color} opacity-0 group-hover:opacity-10 rounded-2xl blur-xl transition-opacity duration-300`}></div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mt-16 text-center"
          >
            <Button 
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white text-base px-8 py-6 rounded-full hover:-translate-y-1 transition-all shadow-lg shadow-cyan-500/25"
              onClick={() => navigate('/register')}
              data-testid="advanced-features-cta"
            >
              {t('landing.discoverFree')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 md:py-32 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4 block">
              {t('landing.testimonials')}
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold tracking-tight">
              {t('landing.trustedBy')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-zinc-800 border border-zinc-700 p-8 hover:border-zinc-600 transition-all"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-zinc-300 mb-6 text-lg leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-zinc-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section id="pricing" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4 block">
              {t('landing.pricingSection')}
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
              {t('landing.pricingTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-zinc-600">
              {t('landing.pricingSubtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto"
          >
            {[
              { nameKey: "pricing.starter", price: "29", periodKey: "pricing.perMonth", features: ["pricing.features.unlimitedInvoicesQuotes", "pricing.features.pdfTemplates20", "pricing.features.qrBillSwiss", "pricing.features.emailSupport"] },
              { nameKey: "pricing.pro", price: "59", periodKey: "pricing.perMonth", popular: true, originalPrice: "79", features: ["pricing.features.allStarter", "pricing.features.hrPayroll", "pricing.features.multiUser", "pricing.features.shopifySync", "pricing.features.prioritySupport"] },
              { nameKey: "pricing.allInOne", price: "549", periodKey: "pricing.perYear", originalPrice: "708", savings: "23%", features: ["pricing.features.allPro", "pricing.features.annualSaving", "pricing.features.aiAssistant", "pricing.features.stockManagement", "pricing.features.clientPortal", "pricing.features.mobileApp", "pricing.features.dedicatedSupport"] }
            ].map((plan, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className={`border p-8 text-center relative ${
                  plan.popular 
                    ? 'border-red-500 bg-red-50 scale-105' 
                    : 'border-zinc-200 bg-white'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-sm">
                      {t('pricing.popular').toUpperCase()}
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-semibold mb-2">{t(plan.nameKey)}</h3>
                <div className="mb-6">
                  {plan.originalPrice && (
                    <span className="text-lg text-zinc-400 line-through mr-2">{plan.originalPrice}</span>
                  )}
                  <span className="text-5xl font-bold">{plan.price}</span>
                  <span className="text-zinc-500"> CHF{t(plan.periodKey)}</span>
                  {plan.savings && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-sm">
                      -{plan.savings}
                    </span>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center justify-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      {t(feature)}
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full rounded-sm ${
                    plan.popular 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                  }`}
                  onClick={() => navigate('/pricing')}
                  data-testid={`pricing-${index}-btn`}
                >
                  {t('landing.choose')}
                </Button>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-8"
          >
            <p className="text-zinc-500">
              {t('landing.freeTrial')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== DETAILED COMPARISON TABLE ===== */}
      <section id="comparison" className="py-24 md:py-32 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4 block">
              {t('landing.compFeatures')}
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 mb-4">
              {t('landing.comparisonTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-zinc-600">
              {t('landing.comparisonSubtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-white border border-zinc-200 overflow-hidden"
            data-testid="comparison-table"
          >
            {/* Table Header */}
            <div className="grid grid-cols-4 border-b border-zinc-200 bg-zinc-900 text-white sticky top-0 z-10">
              <div className="p-5 text-left font-semibold text-sm">{t('landing.compFeatures')}</div>
              <div className="p-5 text-center">
                <div className="font-semibold text-sm">{t('pricing.starter')}</div>
                <div className="text-xs text-zinc-400 mt-0.5">29 CHF{t('pricing.perMonth')}</div>
              </div>
              <div className="p-5 text-center bg-red-600">
                <div className="font-semibold text-sm">{t('pricing.pro')}</div>
                <div className="text-xs text-red-200 mt-0.5">59 CHF{t('pricing.perMonth')}</div>
              </div>
              <div className="p-5 text-center">
                <div className="font-semibold text-sm">{t('pricing.allInOne')}</div>
                <div className="text-xs text-zinc-400 mt-0.5">549 CHF{t('pricing.perYear')}</div>
              </div>
            </div>

            {/* Table Body */}
            {comparisonCategories.map((category, catIdx) => (
              <div key={catIdx}>
                {/* Category Header */}
                <div className="grid grid-cols-4 bg-zinc-50 border-b border-zinc-200">
                  <div className="col-span-4 p-4 font-bold text-sm text-zinc-900 tracking-wide uppercase">
                    {t(category.nameKey)}
                  </div>
                </div>
                {/* Feature Rows */}
                {category.features.map((feature, fIdx) => (
                  <div 
                    key={fIdx} 
                    className="grid grid-cols-4 border-b border-zinc-100 hover:bg-zinc-50/80 transition-colors"
                  >
                    <div className="p-4 text-sm text-zinc-700">{t(feature.nameKey)}</div>
                    <div className="p-4 flex items-center justify-center">{renderCompCell(feature.starter)}</div>
                    <div className="p-4 flex items-center justify-center bg-red-50/40">{renderCompCell(feature.pro)}</div>
                    <div className="p-4 flex items-center justify-center">{renderCompCell(feature.enterprise)}</div>
                  </div>
                ))}
              </div>
            ))}

            {/* Bottom CTA row */}
            <div className="grid grid-cols-4 bg-zinc-50 border-t border-zinc-200">
              <div className="p-5"></div>
              <div className="p-5 text-center">
                <Button 
                  variant="outline" 
                  className="text-sm rounded-sm"
                  onClick={() => navigate('/pricing')}
                  data-testid="comp-starter-btn"
                >
                  {t('landing.choose')}
                </Button>
              </div>
              <div className="p-5 text-center bg-red-50/40">
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white text-sm rounded-sm"
                  onClick={() => navigate('/pricing')}
                  data-testid="comp-pro-btn"
                >
                  {t('landing.choose')}
                </Button>
              </div>
              <div className="p-5 text-center">
                <Button 
                  className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm rounded-sm"
                  onClick={() => navigate('/pricing')}
                  data-testid="comp-enterprise-btn"
                >
                  {t('landing.choose')}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== PAS ENCORE CONVAINCU? CTA ===== */}
      <section className="py-24 md:py-32 bg-zinc-950 text-white relative overflow-hidden">
        {/* Background texture */}
        <div 
          className="absolute inset-0 opacity-10 mix-blend-overlay"
          style={{
            backgroundImage: `url("https://static.prod-images.emergentagent.com/jobs/65f14d0e-74ec-437d-a428-cb128dce7b8d/images/a2eb4e67d9babb642905b5f7d8affc28f0bd05f545e2a3470bd21575803e011d.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-6">
              {t('landing.notConvinced')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-zinc-400 mb-4 max-w-2xl mx-auto leading-relaxed">
              {t('landing.notConvincedDesc')}
            </motion.p>
            <motion.p variants={fadeInUp} className="text-base text-zinc-500 mb-10">
              {t('landing.notConvincedLimit')}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-10 py-7 rounded-sm hover:-translate-y-1 transition-all shadow-lg shadow-red-900/30"
                onClick={() => navigate('/register')}
                data-testid="not-convinced-cta-button"
              >
                {t('landing.startFreeTrial')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
            <motion.p variants={fadeInUp} className="mt-6 text-sm text-zinc-600">
              {t('landing.noCommitment')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4 block">
              FAQ
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
              {t('landing.faq')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="border-b border-zinc-200"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between py-6 text-left"
                >
                  <span className="font-semibold text-zinc-900">{t(faq.qKey)}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="pb-6 text-zinc-600"
                  >
                    {t(faq.aKey)}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-6">
              {t('landing.readyToStart')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-zinc-400 mb-8">
              {t('landing.joinEntrepreneurs')}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-10 py-7 rounded-sm hover:-translate-y-1 transition-all shadow-lg shadow-red-900/30"
                onClick={() => navigate('/register')}
                data-testid="final-cta-btn"
              >
                {t('landing.createFreeAccount')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src="/xyron-logo.png" alt="Xyron Core ERP" className="h-8 w-8 rounded-lg object-contain" />
              <span className="font-bold text-xl tracking-tight">Xyron Core ERP</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <Link to="/legal" className="hover:text-zinc-900">{t('landing.legalNotice')}</Link>
              <Link to="/privacy" className="hover:text-zinc-900">{t('landing.privacy')}</Link>
              <a href="/register" className="hover:text-zinc-900">{t('landing.contact')}</a>
            </div>
            <p className="text-sm text-zinc-500">
              &copy; 2026 Xyron Core ERP. {t('landing.madeIn')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
